import { IStairConfig, IStepGeometry } from '@stair-craft/common/util';

import { ISpiralLayout } from '../spiral-calculate-layout/calculate-layout';

/** The exit platform's sweep past the last tread (degrees). */
export const EXIT_PLATFORM_ANGLE_DEG: number = 90;

export function generateExitSteps(layout: ISpiralLayout, config: IStairConfig): IStepGeometry[] {
    const { outerRadius, innerRadius, anglePerStepRad, stepCount } = layout;
    const cx: number = outerRadius;
    const cz: number = outerRadius;
    const clockwise: boolean = config.turnDirection === 'right';
    const dirSign: number = clockwise ? 1 : -1;

    // Exit platform reaches to opening edge
    const openingR: number = Math.min(config.openingWidth, config.openingLength) / 2;
    const exitR: number = Math.max(outerRadius, openingR);
    const quarterAngle: number = (EXIT_PLATFORM_ANGLE_DEG * Math.PI) / 180;
    const segCount: number = 6;
    const segAngle: number = quarterAngle / segCount;
    const lastRegularEnd: number = (stepCount - 1) * anglePerStepRad * dirSign;
    const y: number = config.floorHeight + config.floorThickness;
    const treadW: number = exitR - innerRadius;
    // Chord ends touching the pole at the middle (see generateSteps), and the outer edge follows
    // the round opening in short chords so the platform meets the slab without a sliver.
    const innerCornerR: number = innerRadius / Math.cos(segAngle / 2);
    const arcPieces: number = 8;
    const exitSteps: IStepGeometry[] = [];

    for (let s: number = 0; s < segCount; s++) {
        const sStart: number = lastRegularEnd + s * segAngle * dirSign;
        const sEnd: number = lastRegularEnd + (s + 1) * segAngle * dirSign;
        const sMid: number = lastRegularEnd + (s + 0.5) * segAngle * dirSign;
        const oDep: number = Math.round(2 * exitR * Math.sin(segAngle / 2));
        const iDep: number = Math.round(2 * innerRadius * Math.sin(segAngle / 2));
        const centR: number = (innerRadius + exitR) / 2;

        exitSteps.push({
            index: stepCount - 1 + s,
            position: {
                y,
                x: cx + centR * Math.sin(sMid) - treadW / 2,
                z: cz + centR * Math.cos(sMid) - oDep / 2,
            },
            treadWidth: treadW,
            treadDepth: oDep,
            treadThickness: config.treadThickness,
            // The platform is one level: only its first segment has a riser and a nose over the
            // last step; the rest butt edge to edge.
            riserHeight: s === 0 ? layout.riserHeight : 0,
            noseLength: s === 0 ? config.noseLength : 0,
            rotation: -(sMid * 180) / Math.PI,
            isWinder: true,
            isLanding: false,
            innerDepth: iDep,
            outerDepth: oDep,
            winderCorners: {
                innerBack: { x: cx + innerCornerR * Math.sin(sStart), z: cz + innerCornerR * Math.cos(sStart) },
                innerFront: { x: cx + innerCornerR * Math.sin(sEnd), z: cz + innerCornerR * Math.cos(sEnd) },
                outerBack: { x: cx + exitR * Math.sin(sStart), z: cz + exitR * Math.cos(sStart) },
                outerFront: { x: cx + exitR * Math.sin(sEnd), z: cz + exitR * Math.cos(sEnd) },
                outerMidPoints: Array.from({ length: arcPieces - 1 }, (_: unknown, k: number) => {
                    const angle: number = sStart + ((k + 1) / arcPieces) * (sEnd - sStart);
                    return { x: cx + exitR * Math.sin(angle), z: cz + exitR * Math.cos(angle) };
                }),
            },
        });
    }

    return exitSteps;
}
